import { useEffect, useMemo, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { Skeleton } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Box, F0Dialog } from '@factorialco/f0-react'
import { EntityId, EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'

import { InfoCircle } from '@factorialco/f0-react/icons/app'
import {
  AddGroupModalTrainingsWithClassesDocument,
  AssignBudgetToTrainingClassDocument,
} from 'generated/resources/graphql'

import { EntitySelectWrapper } from 'modules/core/components/EntitySelectWrapper'
import { flatSelection } from 'modules/core/components/EntitySelectWrapper/lib/flat'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { useExchangeRate } from 'modules/expenses/hooks/useExchangeRate'

import {
  calculateConvertedAllocationCents,
  getSelectedEntities,
  hasForeignCurrency,
  mapTrainingsToEntities,
} from 'modules/finance/components/TrainingsBudgetWizard/AllocationStep/lib/trainingEntityMapper'

import { BudgetSummaryCards } from 'modules/finance/components/TrainingsBudgetWizard/AllocationStep/BudgetSummaryCards'

interface Props {
  budgetId: string
  currency: string
  availableAmountCents: number
  totalBudgetCents: number
  onClose: () => void
}

export const AddGroupModal = ({
  budgetId,
  currency,
  availableAmountCents,
  totalBudgetCents,
  onClose,
}: Props) => {
  const apolloClient = useApolloClient()
  const [selectedClassIds, setSelectedClassIds] = useState<string[]>([])
  const [expandedElements, setExpandedElements] = useState<EntityId[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [exchangeRate, setExchangeRate] = useState<number>(1)

  const { data: trainingsData, loading } = useQuery(AddGroupModalTrainingsWithClassesDocument)

  const trainings = useMemo(() => {
    const allTrainings =
      trainingsData?.trainings?.trainingsConnection?.nodes?.filter(
        (node): node is NonNullable<typeof node> => node !== null
      ) ?? []

    return allTrainings
      .filter((training) => training._policies?.edit?.can)
      .map((training) => ({
        ...training,
        trainingClassesConnection: {
          ...training.trainingClassesConnection,
          nodes: training.trainingClassesConnection.nodes.filter((cls) => !cls.budgetMovement),
        },
      }))
      .filter((training) => training.trainingClassesConnection.nodes.length > 0)
  }, [trainingsData])

  const needsConversion = useMemo(
    () => hasForeignCurrency(trainings, currency),
    [trainings, currency]
  )

  const { fetch: fetchExchangeRate, loading: loadingRate } = useExchangeRate({
    onCompleted: ({ rate }) => setExchangeRate(rate),
  })

  useEffect(() => {
    if (!trainings.length || !needsConversion) return
    const firstClass = trainings[0]?.trainingClassesConnection.nodes[0]
    if (!firstClass) return
    fetchExchangeRate({
      baseCurrency: firstClass.currency,
      targetCurrency: currency,
      date: new Date().toISOString().split('T')[0],
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainings, needsConversion, currency])

  const entities = useMemo(
    () => mapTrainingsToEntities(trainings, expandedElements),
    [trainings, expandedElements]
  )

  const selectedEntities = useMemo(
    () => getSelectedEntities(entities, selectedClassIds),
    [entities, selectedClassIds]
  )

  const selectedCostCents = useMemo(
    () => calculateConvertedAllocationCents(trainings, selectedClassIds, exchangeRate),
    [trainings, selectedClassIds, exchangeRate]
  )

  const allocationCents = selectedCostCents
  const availableCents = availableAmountCents - selectedCostCents

  const [assignBudgetToTrainingClass] = useMutation(AssignBudgetToTrainingClassDocument, {
    extractorFn: (data) => data.trainingsMutations.updateTrainingsTrainingClass,
  })

  const handleSelectMultiple = (selection: EntitySelectEntity[] | null) => {
    if (!selection) {
      setSelectedClassIds([])
      return
    }
    const classIds = flatSelection(selection).map((item) => String(item.subId))
    setSelectedClassIds(classIds)
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      await Promise.all(
        selectedClassIds.map((classId) =>
          assignBudgetToTrainingClass({
            variables: { id: classId, budgetId },
          })
        )
      )
      await apolloClient.refetchQueries({
        include: [
          'FinanceBudgetDetailTrainingResourceDataCollection',
          'FinanceBudgetDetailRevampPage',
          'FinanceBudgetDetailAmountWidgets',
        ],
      })
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t('budgets.add_group_modal.title')}
      width="xl"
      primaryAction={{
        label: i18n.t('budgets.add_group_modal.add_button'),
        onClick: handleSubmit,
        disabled: selectedClassIds.length === 0 || submitting,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <F0Box display="flex" flexDirection="column" gap="xl">
        <BudgetSummaryCards
          totalBudgetCents={totalBudgetCents}
          allocationCents={allocationCents}
          availableCents={availableCents}
          currency={currency}
          loading={loadingRate}
        />

        <F0Box borderBottom="default" borderColor="info" />

        {loading ? (
          <F0Box flexDirection="column" gap="md">
            <Skeleton height="s64" width="full" />
            <Skeleton height="s64" width="full" />
          </F0Box>
        ) : (
          <EntitySelectWrapper
            size="md"
            width="s600"
            entities={entities}
            groups={[{ label: i18n.t('common.all'), value: 'all' }]}
            expandedElements={expandedElements}
            selectedGroup="all"
            selectedGroupedEntities={selectedEntities}
            loading={loading}
            multiple
            hiddenAvatar
            singleSelector={false}
            label={i18n.t('budgets.add_group_modal.select_label')}
            placeholder={i18n.t('budgets.add_group_modal.select_placeholder')}
            searchPlaceholder={i18n.t('common.search')}
            selectAllLabel={i18n.t('common.select_all')}
            clearLabel={i18n.t('common.clear')}
            selectedLabel={i18n.t('common.selected')}
            selectedItemsCopy={i18n.t(
              'budgets.forms.trainings_budget.allocation.trainings_selected'
            )}
            notFoundTitle={i18n.t('budgets.forms.trainings_budget.allocation.no_trainings')}
            notFoundSubtitle=""
            onSelect={() => {}}
            onSelectMultiple={handleSelectMultiple}
            onGroupChange={() => {}}
            onSelectExpandedElements={setExpandedElements}
            required
          />
        )}

        {needsConversion && exchangeRate !== 1 && (
          <F0Alert
            variant="info"
            icon={InfoCircle}
            title={i18n.t('budgets.forms.trainings_budget.allocation.conversion_info_title')}
            description={i18n.t(
              'budgets.forms.trainings_budget.allocation.conversion_info_description'
            )}
          />
        )}

        <F0Alert
          variant="neutral"
          icon={InfoCircle}
          title={i18n.t('budgets.forms.trainings_budget.allocation.info_title')}
          description={i18n.t('budgets.forms.trainings_budget.allocation.info_description')}
        />
      </F0Box>
    </F0Dialog>
  )
}
